import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import ProLayout from '@/components/layouts/ProLayout';
import { 
  LayoutDashboard, Users, BookOpen, UserCog, Video, BarChart3, 
  ClipboardList, Calendar, Gamepad2, FileText, PenTool, Languages,
  ChevronDown, ChevronRight, Search, BookMarked, CheckCircle2,
  AlertCircle, Sparkles
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const AdminCurriculumBrowser = () => {
  const { api } = useAuth();
  const [curriculum, setCurriculum] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedKeyStage, setSelectedKeyStage] = useState('ks1');
  const [selectedSubject, setSelectedSubject] = useState('maths');
  const [expandedTopics, setExpandedTopics] = useState({});
  const [expandedLessons, setExpandedLessons] = useState({});
  const [searchQuery, setSearchQuery] = useState('');

  const sidebarItems = [
    { path: '/admin', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/users', label: 'User Management', icon: Users },
    { path: '/admin/allocate', label: 'Subject Allocation', icon: BookOpen },
    { path: '/admin/tutors', label: 'Tutor Assignment', icon: UserCog },
    { path: '/tutoring', label: 'Live Sessions', icon: Video },
    { path: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
    { path: '/admin/assessments', label: 'Assessments', icon: ClipboardList },
    { path: '/admin/reports', label: 'Monthly Reports', icon: Calendar },
    { divider: true, label: 'Content Preview' },
    { path: '/admin/curriculum-browser', label: 'Curriculum Browser', icon: BookMarked, active: true },
    { path: '/admin/preview/games', label: 'Games Zone', icon: Gamepad2 },
    { path: '/admin/preview/exams', label: 'Exams', icon: FileText },
    { path: '/admin/preview/essays', label: 'Essays', icon: PenTool },
    { path: '/admin/preview/esol-videos', label: 'ESOL Videos', icon: Languages },
  ];

  const keyStages = [
    { id: 'ks1', name: 'Key Stage 1', years: 'Years 1-2', color: 'emerald' },
    { id: 'ks2', name: 'Key Stage 2', years: 'Years 3-6', color: 'blue' },
    { id: 'ks3', name: 'Key Stage 3', years: 'Years 7-9', color: 'purple' },
    { id: 'ks4', name: 'Key Stage 4', years: 'Years 10-11', color: 'amber' },
  ];

  const subjects = [
    { id: 'maths', name: 'Maths', color: 'amber' },
    { id: 'eng', name: 'English', color: 'rose' },
    { id: 'sci', name: 'Science', color: 'emerald' },
  ];

  useEffect(() => {
    fetchCurriculum();
  }, []);

  const fetchCurriculum = async () => {
    try {
      const [curriculumRes, summaryRes] = await Promise.all([
        api.get('/admin/curriculum/all'),
        api.get('/admin/curriculum/summary')
      ]);
      setCurriculum(curriculumRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Error fetching curriculum:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleTopic = (topicId) => {
    setExpandedTopics(prev => ({
      ...prev,
      [topicId]: !prev[topicId]
    }));
  };

  const toggleLesson = (topicId, lessonNum) => {
    const key = `${topicId}-${lessonNum}`;
    setExpandedLessons(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'easy': return 'bg-green-100 text-green-700';
      case 'medium': return 'bg-amber-100 text-amber-700';
      case 'hard': return 'bg-red-100 text-red-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getCurrentTopics = () => {
    if (!curriculum?.curriculum) return [];
    const ksData = curriculum.curriculum[selectedKeyStage];
    if (!ksData) return [];
    const subjectData = ksData[selectedSubject];
    if (!subjectData) return [];
    
    let topics = Object.values(subjectData);
    
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      topics = topics.filter(topic => 
        topic.topic_name.toLowerCase().includes(query) ||
        topic.topic_id.toLowerCase().includes(query)
      );
    }
    
    return topics;
  };

  if (loading) {
    return (
      <ProLayout title="Curriculum Browser" sidebarItems={sidebarItems}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-600 border-t-transparent"></div>
        </div>
      </ProLayout>
    );
  }

  const currentTopics = getCurrentTopics();

  return (
    <ProLayout title="Curriculum Browser" sidebarItems={sidebarItems}>
      <div data-testid="curriculum-browser">
        {/* Statistics Banner */}
        {curriculum?.statistics && (
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="pro-card bg-gradient-to-r from-emerald-50 to-teal-50">
              <div className="text-2xl font-bold text-emerald-700">{curriculum.statistics.total_topics}</div>
              <div className="text-sm text-slate-600">Total Topics</div>
            </div>
            <div className="pro-card bg-gradient-to-r from-blue-50 to-indigo-50">
              <div className="text-2xl font-bold text-blue-700">{curriculum.statistics.total_lessons}</div>
              <div className="text-sm text-slate-600">Total Lessons</div>
            </div>
            <div className="pro-card bg-gradient-to-r from-purple-50 to-pink-50">
              <div className="text-2xl font-bold text-purple-700">{curriculum.statistics.total_questions}</div>
              <div className="text-sm text-slate-600">Total Questions</div>
            </div>
          </div>
        )}

        {/* Key Stage Selector */}
        <div className="flex gap-2 mb-4">
          {keyStages.map(ks => (
            <button
              key={ks.id}
              onClick={() => setSelectedKeyStage(ks.id)}
              data-testid={`ks-tab-${ks.id}`}
              className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                selectedKeyStage === ks.id
                  ? `bg-${ks.color}-500 text-white shadow-md`
                  : `bg-${ks.color}-100 text-${ks.color}-700 hover:bg-${ks.color}-200`
              }`}
            >
              {ks.name}
              <span className="text-xs ml-1 opacity-75">({ks.years})</span>
            </button>
          ))}
        </div>

        {/* Subject Selector */}
        <div className="flex gap-2 mb-6">
          {subjects.map(subject => (
            <button
              key={subject.id}
              onClick={() => setSelectedSubject(subject.id)}
              data-testid={`subject-tab-${subject.id}`}
              className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                selectedSubject === subject.id
                  ? `bg-${subject.color}-500 text-white shadow-md`
                  : `bg-slate-100 text-slate-700 hover:bg-slate-200`
              }`}
            >
              {subject.name}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
            <Input
              placeholder="Search topics..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="topic-search"
            />
          </div>
        </div>

        {/* Topics List */}
        <div className="space-y-4" data-testid="topics-list">
          {currentTopics.length === 0 ? (
            <div className="pro-card text-center py-8">
              <AlertCircle className="w-12 h-12 mx-auto text-slate-400 mb-3" />
              <p className="text-slate-600">No topics found for this selection.</p>
            </div>
          ) : (
            currentTopics.map(topic => (
              <div key={topic.topic_id} className="pro-card" data-testid={`topic-${topic.topic_id}`}>
                {/* Topic Header */}
                <button
                  onClick={() => toggleTopic(topic.topic_id)}
                  className="w-full flex items-center justify-between p-2 hover:bg-slate-50 rounded-lg transition-colors"
                  data-testid={`topic-toggle-${topic.topic_id}`}
                >
                  <div className="flex items-center gap-3">
                    {expandedTopics[topic.topic_id] ? (
                      <ChevronDown className="w-5 h-5 text-slate-500" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-slate-500" />
                    )}
                    <div className="text-left">
                      <h3 className="font-semibold text-slate-800">{topic.topic_name}</h3>
                      <p className="text-sm text-slate-500">
                        {topic.total_lessons} lessons • {Object.values(topic.lessons).reduce((sum, l) => sum + l.question_count, 0)} questions
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-semibold">
                      {topic.total_lessons} Lessons
                    </span>
                  </div>
                </button>

                {/* Expanded Topic - Lessons */}
                {expandedTopics[topic.topic_id] && (
                  <div className="mt-4 ml-8 space-y-3">
                    {Object.entries(topic.lessons).map(([lessonNum, lesson]) => (
                      <div key={lessonNum} className="border rounded-lg" data-testid={`lesson-${topic.topic_id}-${lessonNum}`}>
                        <button
                          onClick={() => toggleLesson(topic.topic_id, lessonNum)}
                          className="w-full flex items-center justify-between p-3 hover:bg-slate-50 transition-colors"
                          data-testid={`lesson-toggle-${topic.topic_id}-${lessonNum}`}
                        >
                          <div className="flex items-center gap-3">
                            {expandedLessons[`${topic.topic_id}-${lessonNum}`] ? (
                              <ChevronDown className="w-4 h-4 text-slate-400" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-slate-400" />
                            )}
                            <span className="font-medium text-slate-700">Lesson {lessonNum}</span>
                          </div>
                          <span className="text-sm text-slate-500">{lesson.question_count} questions</span>
                        </button>

                        {/* Expanded Lesson - Questions */}
                        {expandedLessons[`${topic.topic_id}-${lessonNum}`] && (
                          <div className="border-t bg-slate-50 p-4 space-y-3">
                            {lesson.questions.map((question, qIdx) => (
                              <div key={qIdx} className="bg-white rounded-lg p-4 border" data-testid={`question-${topic.topic_id}-${lessonNum}-${qIdx}`}>
                                <div className="flex items-start justify-between mb-2">
                                  <span className="font-semibold text-slate-800">Q{qIdx + 1}.</span>
                                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${getDifficultyColor(question.difficulty)}`}>
                                    {question.difficulty}
                                  </span>
                                </div>
                                <p className="text-slate-700 mb-3">{question.question}</p>
                                
                                <div className="grid grid-cols-2 gap-2 mb-3">
                                  {question.options.map((option, optIdx) => (
                                    <div 
                                      key={optIdx}
                                      className={`p-2 rounded text-sm ${
                                        optIdx === question.correct_answer
                                          ? 'bg-green-100 text-green-800 border-2 border-green-500'
                                          : 'bg-slate-100 text-slate-600'
                                      }`}
                                    >
                                      <span className="font-medium mr-1">{String.fromCharCode(65 + optIdx)}.</span>
                                      {option}
                                      {optIdx === question.correct_answer && (
                                        <CheckCircle2 className="inline w-4 h-4 ml-1 text-green-600" />
                                      )}
                                    </div>
                                  ))}
                                </div>
                                
                                <div className="bg-blue-50 rounded p-2 text-sm text-blue-800">
                                  <strong>Explanation:</strong> {question.explanation}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </ProLayout>
  );
};

export default AdminCurriculumBrowser;
